import { useState, useEffect, useCallback } from 'react';
import { streakAPI, StreakFilters, StreakStats } from '../lib/api/streak';

export interface UseStreakStatsResult {
    streakData: StreakStats | null;
    isLoading: boolean;
    error: string | null;
    refreshStreakData: (timeRange?: 'week' | 'month' | 'year' | 'all') => Promise<void>;
    resetStreak: () => Promise<void>;
}

export interface UseStreakStatsOptions extends StreakFilters {
    autoRefresh?: boolean;
    refreshInterval?: number; // en milisegundos
}

/**
 * Hook personalizado para obtener y gestionar estadísticas de rachas
 */
export function useStreakStats({
    timeRange = 'all',
    autoRefresh = false,
    refreshInterval = 60000 // 1 minuto por defecto
}: UseStreakStatsOptions = {}): UseStreakStatsResult {
    const [streakData, setStreakData] = useState<StreakStats | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Función para obtener los datos de racha
    const fetchStreakData = useCallback(async (range: 'week' | 'month' | 'year' | 'all' = timeRange) => {
        setIsLoading(true);
        setError(null);

        try {
            const data = await streakAPI.getStreakStats({
                timeRange: range
            });

            setStreakData(data);
        } catch (err) {
            console.error('Error fetching streak data:', err);
            setError(err instanceof Error ? err.message : 'Error desconocido al obtener datos de racha');
        } finally {
            setIsLoading(false);
        }
    }, [timeRange]);

    // Función para restablecer la racha
    const resetStreak = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            await streakAPI.resetStreak();

            // Refrescar los datos después del reinicio
            await fetchStreakData();
        } catch (err) {
            console.error('Error resetting streak:', err);
            setError(err instanceof Error ? err.message : 'Error desconocido al restablecer racha');
        } finally {
            setIsLoading(false);
        }
    }, [fetchStreakData]);

    // Efecto para cargar los datos inicialmente
    useEffect(() => {
        fetchStreakData();
    }, [fetchStreakData]);

    // Configurar actualización automática si está habilitada
    useEffect(() => {
        if (!autoRefresh) return;

        const intervalId = setInterval(() => {
            fetchStreakData();
        }, refreshInterval);

        return () => clearInterval(intervalId);
    }, [autoRefresh, refreshInterval, fetchStreakData]);

    return {
        streakData,
        isLoading,
        error,
        refreshStreakData: fetchStreakData,
        resetStreak
    };
}

export default useStreakStats;